// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import Icon from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import IconBreadcrumbsAngle from "@stoneage/XDL/Icons/IconBreadcrumbsAngle";
import * as React from "react";
import styled, { withTheme } from "styled-components";
import { ColorTheme } from "@stoneage/App/Types/theme";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

// XNG-Change
// export const Slash = styled(Typography as any).attrs({ children: "/" })`
//     margin: 0 10px;
//     display: block;
// `;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

// XNG-Changes
type Props = BreadcrumbsProps & { colorTheme?: ColorTheme };

const Breadcrumbs: React.FC<Props> = withTheme(({ links, mergeCss, colorTheme = "light", ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    const dispatch = useAppDispatch();
    const currentPageType = useAppSelector(state => getCurrentPage(state).type);

    // XNG-Change
    const resetBomState = () => {
        dispatch({ type: "xCommon/update", data: { key: "isBomView", value: false } });
    };

    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }

    // XNG changes : Get last two elements of the links
    links = ["ProductListPage", "CategoryDetailsPage"].includes(currentPageType) ? links.slice(-3) : links.slice(-2);
    const children = links.map((link: any, index: number) => {
        const children = link.children;
        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }

        const key = typeof children === "string" ? `${children}_${index}` : index;
        const Slash = (
            <Icon width={5} height={9} color="common.ui6" src={IconBreadcrumbsAngle} className="breadcrumb_slash" />
        );

        if (index === links.length - 1 || !link.href) {
            return (
                <React.Fragment key={key}>
                    <Typography
                        mergeCss={resolvedMergeCss}
                        {...typographyProps}
                        aria-current="page"
                        key={key}
                        data-test-selector={`breadCrumb_${children}`}
                        color={colorTheme === "light" ? typographyProps.color : "common.white"}
                        className="breadcrumb_text"
                    >
                        {children}
                    </Typography>

                    {/* XNG-Change  */}
                    {!link.href && index < links.length - 1 && <>{Slash}</>}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={key}>
                <Link
                    typographyProps={typographyProps}
                    color={colorTheme === "light" ? typographyProps.color : "common.white"}
                    {...link}
                    className="breadcrumb_link"
                    onClick={() => {
                        resetBomState();
                        link.onClick();
                    }}
                />
                {/* XNG-Change  */}
                {Slash}
            </React.Fragment>
        );
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    return (
        <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
            {children}
        </StyledNav>
    );
});

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
